import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildAvailabilityWindows,
  dateKeyInTimezone,
  formatMonthKey,
  freshWindow,
} from './slotPickerUtils';

export interface AvailabilitySlot {
  start_time: string;
}

export interface UseMonthAvailabilityOptions<T extends AvailabilitySlot> {
  /** The hook fetches only while this is true. */
  enabled: boolean;
  /** A change to this key starts a new fetch, for example a new coach id. */
  sourceKey: string;
  /** The viewer's IANA timezone. Slots are grouped by day in this timezone. */
  timezone: string;
  /** Fetch the slots for one window. The hook never asks for more than 7 days. */
  fetchWindow: (startIso: string, endIso: string) => Promise<{ slots: T[] }>;
  /** When set, a window that takes longer than this fails. */
  timeoutMs?: number;
  /**
   * When the first month loads with no times and no errors, move ahead one
   * month, up to this many times. Stops when the viewer moves the month.
   * Defaults to 0 (off).
   */
  autoAdvanceMonths?: number;
}

export interface MonthAvailability<T extends AvailabilitySlot> {
  today: Date;
  viewYear: number;
  viewMonth: number;
  prevMonth: () => void;
  nextMonth: () => void;
  /** True when the visible month is the current month or earlier. */
  isPrevDisabled: boolean;
  /** Slots grouped by "YYYY-MM-DD" in the viewer's timezone, for all months loaded so far. */
  slotsByDate: Map<string, T[]>;
  isLoading: boolean;
  /** The first error when every window of the last fetch failed, else null. */
  failure: unknown;
  /** True when some windows of the last fetch failed, but not all of them. */
  partialFailure: boolean;
  /** The "YYYY-MM" key of the last month that finished loading, else null. */
  loadedMonthKey: string | null;
  /**
   * How many months in a row the hook found empty, up to and including the
   * visible month, when it skipped empty months on its own. 0 when the
   * visible month has times, when a window failed, or after the viewer
   * changed the month.
   */
  emptyMonthsSearched: number;
  /** Go back to the current month and clear all slots. */
  reset: () => void;
  /** Clear all slots and fetch the visible month again. */
  reload: () => void;
}

/**
 * Load a coach's free slots for the visible month in 7-day windows.
 * Slots from earlier months stay in the map when the viewer moves to a new month.
 */
export function useMonthAvailability<T extends AvailabilitySlot>({
  enabled,
  sourceKey,
  timezone,
  fetchWindow,
  timeoutMs,
  autoAdvanceMonths = 0,
}: UseMonthAvailabilityOptions<T>): MonthAvailability<T> {
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [slotsByDate, setSlotsByDate] = useState<Map<string, T[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [partialFailure, setPartialFailure] = useState(false);
  const [loadedMonthKey, setLoadedMonthKey] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // How many empty months the hook may still skip on its own.
  const autoAdvanceLeft = useRef(autoAdvanceMonths);
  // How many empty months the hook skipped on its own before this month.
  const skippedEmptyMonths = useRef(0);
  const [emptyMonthsSearched, setEmptyMonthsSearched] = useState(0);

  // Keep the latest fetch function in a ref, so that a new function identity
  // on each render does not start a new fetch.
  const fetchRef = useRef(fetchWindow);
  useEffect(() => {
    fetchRef.current = fetchWindow;
  }, [fetchWindow]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    let advanced = false;

    const fetchAvailability = async () => {
      setIsLoading(true);
      setFailure(null);
      setPartialFailure(false);
      try {
        const monthStart = new Date(viewYear, viewMonth, 1);
        const monthEnd = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59);
        const now = new Date();
        const fetchStart = monthStart < now ? now : monthStart;

        const windows = buildAvailabilityWindows(fetchStart, monthEnd);
        const totalWindows = windows.length;

        // Fetch one window. With timeoutMs set, a slow window fails.
        const fetchOne = async (windowStart: Date, windowEnd: Date) => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          try {
            const request = fetchRef.current(
              windowStart.toISOString(),
              windowEnd.toISOString()
            );
            const result = timeoutMs
              ? await Promise.race([
                  request,
                  new Promise<never>((_, reject) => {
                    timer = setTimeout(
                      () => reject(new Error('Request timed out')),
                      timeoutMs
                    );
                  }),
                ])
              : await request;
            return result.slots;
          } finally {
            if (timer) clearTimeout(timer);
          }
        };

        // Fetch all windows of the month at the same time.
        const results = await Promise.allSettled(
          windows.map(w => fetchOne(w.start, w.end))
        );
        if (cancelled) return;

        // Try each failed window once more, because one slow or failed call
        // must not hide a whole week. MED-1365: the retry starts no earlier
        // than now, because Calendly refuses a start in the past.
        const failedIndexes = results.flatMap((r, i) =>
          r.status === 'rejected' ? [i] : []
        );
        if (failedIndexes.length > 0 && failedIndexes.length < windows.length) {
          const retries = await Promise.allSettled(
            failedIndexes.map(i => {
              const w = freshWindow(windows[i]);
              return w ? fetchOne(w.start, w.end) : Promise.resolve([] as T[]);
            })
          );
          if (cancelled) return;
          retries.forEach((r, n) => {
            results[failedIndexes[n]] = r;
          });
        }

        const allSlots: T[] = [];
        let failedWindows = 0;
        let firstError: unknown = null;
        for (const result of results) {
          if (result.status === 'fulfilled') {
            allSlots.push(...result.value);
          } else {
            failedWindows++;
            if (firstError === null) firstError = result.reason;
          }
        }

        // An empty month with no errors: skip to the next month, so that the
        // viewer does not land on a dead month at the end of a month.
        if (
          autoAdvanceLeft.current > 0 &&
          failedWindows === 0 &&
          allSlots.length === 0
        ) {
          autoAdvanceLeft.current -= 1;
          skippedEmptyMonths.current += 1;
          advanced = true;
          if (viewMonth === 11) {
            setViewYear(y => y + 1);
            setViewMonth(0);
          } else {
            setViewMonth(m => m + 1);
          }
          return;
        }
        autoAdvanceLeft.current = 0;
        setEmptyMonthsSearched(
          failedWindows === 0 &&
            allSlots.length === 0 &&
            skippedEmptyMonths.current > 0
            ? skippedEmptyMonths.current + 1
            : 0
        );

        if (failedWindows === totalWindows && totalWindows > 0) {
          setFailure(firstError ?? new Error('Unable to load availability'));
        } else if (failedWindows > 0) {
          setPartialFailure(true);
        }

        if (allSlots.length > 0) {
          setSlotsByDate(prev => {
            const updated = new Map(prev);
            for (const slot of allSlots) {
              const dateKey = dateKeyInTimezone(slot.start_time, timezone);
              const existing = updated.get(dateKey) ?? [];
              if (!existing.some(s => s.start_time === slot.start_time)) {
                existing.push(slot);
              }
              updated.set(dateKey, existing);
            }
            return updated;
          });
        }
        setLoadedMonthKey(formatMonthKey(viewYear, viewMonth));
      } finally {
        // After an automatic month change, the next fetch keeps the loader on.
        if (!cancelled && !advanced) {
          setIsLoading(false);
        }
      }
    };

    fetchAvailability();
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    sourceKey,
    viewYear,
    viewMonth,
    reloadToken,
    timezone,
    timeoutMs,
  ]);

  const prevMonth = useCallback(() => {
    autoAdvanceLeft.current = 0;
    skippedEmptyMonths.current = 0;
    if (viewMonth === 0) {
      setViewYear(y => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth(m => m - 1);
    }
  }, [viewMonth]);

  const nextMonth = useCallback(() => {
    autoAdvanceLeft.current = 0;
    skippedEmptyMonths.current = 0;
    if (viewMonth === 11) {
      setViewYear(y => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth(m => m + 1);
    }
  }, [viewMonth]);

  const isPrevDisabled =
    viewYear < today.getFullYear() ||
    (viewYear === today.getFullYear() && viewMonth <= today.getMonth());

  const reset = useCallback(() => {
    autoAdvanceLeft.current = autoAdvanceMonths;
    skippedEmptyMonths.current = 0;
    setEmptyMonthsSearched(0);
    setSlotsByDate(new Map());
    setFailure(null);
    setPartialFailure(false);
    setLoadedMonthKey(null);
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  }, [today, autoAdvanceMonths]);

  const reload = useCallback(() => {
    setSlotsByDate(new Map());
    setFailure(null);
    setPartialFailure(false);
    setLoadedMonthKey(null);
    setReloadToken(t => t + 1);
  }, []);

  return {
    today,
    viewYear,
    viewMonth,
    prevMonth,
    nextMonth,
    isPrevDisabled,
    slotsByDate,
    isLoading,
    failure,
    partialFailure,
    loadedMonthKey,
    emptyMonthsSearched,
    reset,
    reload,
  };
}
